import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '../../notifications';
import { signConversation, toggleAiAssistant } from '../services/conversationService';
import { Conversation } from '../types/Conversation';
import { isExternalConversation } from '../utils/conversationClassification';

type ShowToastHandler = ReturnType<typeof useToast>['showToast'];

interface UseConversationActionsOptions {
  selectedConv: Conversation | null;
  setSelectedConv: Dispatch<SetStateAction<Conversation | null>>;
  setList: Dispatch<SetStateAction<Conversation[]>>;
  showToast: ShowToastHandler;
}

interface UseConversationActionsResult {
  signing: boolean;
  toggleAiAssistantBusy: boolean;
  onSign: () => Promise<void>;
  onToggleAiAssistant: () => Promise<void>;
}

export const useConversationActions = ({
  selectedConv,
  setSelectedConv,
  setList,
  showToast,
}: UseConversationActionsOptions): UseConversationActionsResult => {
  const { t } = useTranslation('conversations');
  const [signing, setSigning] = useState(false);
  const [toggleAiAssistantBusy, setToggleAiAssistantBusy] = useState(false);

  const onSign = useCallback(async () => {
    if (!selectedConv || !isExternalConversation(selectedConv) || signing || selectedConv.isSigned) return;

    setSigning(true);
    try {
      const updated = await signConversation(selectedConv.id);
      if (updated) {
        setSelectedConv(updated);
        setList(current => current.map(conversation => conversation.id === updated.id
          ? { ...conversation, isSigned: true, updatedAt: updated.updatedAt }
          : conversation));
        showToast(t('conversationSignedSuccessfully'), 'success');
      }
    } catch {
      showToast(t('failedToSignConversation'), 'error');
    } finally {
      setSigning(false);
    }
  }, [selectedConv, setList, setSelectedConv, showToast, signing, t]);

  const onToggleAiAssistant = useCallback(async () => {
    if (!selectedConv || !isExternalConversation(selectedConv) || toggleAiAssistantBusy) return;

    const newMutedState = !selectedConv.isAiAssistantMuted;
    setToggleAiAssistantBusy(true);
    try {
      const updated = await toggleAiAssistant(selectedConv.id, newMutedState);
      if (updated) {
        setSelectedConv(updated);
        setList(current => current.map(conversation => conversation.id === updated.id
          ? { ...conversation, isAiAssistantMuted: newMutedState, updatedAt: updated.updatedAt }
          : conversation));
        showToast(newMutedState ? t('aiAssistantMutedToast') : t('aiAssistantActivatedToast'), 'info');
      }
    } catch (error) {
      console.error('Failed to toggle AI assistant', error);
      showToast(t('failedToToggleAiAssistant'), 'error');
    } finally {
      setToggleAiAssistantBusy(false);
    }
  }, [selectedConv, setList, setSelectedConv, showToast, t, toggleAiAssistantBusy]);

  return {
    signing,
    toggleAiAssistantBusy,
    onSign,
    onToggleAiAssistant,
  };
};